export default function(ngModule) {
    /**
     * @ngdoc service
     * @name eventix.common.models.ShopCoupon
     * @kind class
     * @description
     * Model that is used in the shop to calculate ticket prices when a coupon is applied.
     * This model does *not* inherit from `base-class`
     */
    ngModule.factory('ShopCoupon', function(BaseClass,$q, $http) {

        /**
         * Constructor function
         *
         * @param {Object} attributes Initial attributes for the model
         */
        function ShopCoupon(attributes) {
            _.assign(this,attributes);

            // Array with productReservation guids this coupon is applied to
            this.appliedTo = [];
        }
        ShopCoupon.modelName = 'ShopCoupon';

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#get
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Query the API if code represents a valid coupon and return
         * an instance of ShopCoupon if it does.
         *
         * @param {String} shopGuid The current shop
         * @param {String} code Coupon code user has entered
         * @returns {Promise} Resolves with a new ShopCoupon or rejects with `$http` error
         */
        ShopCoupon.get = function(shopGuid, code) {
            return $http.get(`/${shopGuid}/coupon/${code}`).then(response => {
                return new ShopCoupon(response.data);
            });
        };


        //BaseClass.apply(ShopCoupon);

        ShopCoupon.prototype.toString = function() { return this.name; };

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#appliesToProduct
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Check if this coupon can be applied to this product.
         *
         * If coupon is already taken or if product is not attached, then false is returned
         *
         * If coupon would make the product more expensive, false is returned
         *
         * @param {Product} product The product to check
         * @returns {Boolean} True if it can be applied
         */
        ShopCoupon.prototype.appliesToProduct = function(product) {
            if(this.appliedTo.length >= this.applies_to_count && this.applies_to_count > 0)
                return false;

            if (this.coupon.type === 'new-price' && product.price < this.coupon.amount) {
                return false;
            }

            let applicable = _.find(this.coupon.products, { guid: product.guid });
            if(!applicable)
                return false;

            let maxForProduct = _.get(applicable, 'pivot.max_per_coupon', 0);
            if(maxForProduct === 0)
                return true;

            let alreadyApplied = _.filter(this.appliedTo, { productGuid: product.guid });
            return alreadyApplied.length < maxForProduct;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#isAppliedTo
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Check if coupon is applied to a reservation
         * @param {String} reservationId The product reservation ID to check
         * @returns {Boolean} True if the coupon is applied to this product
         */
        ShopCoupon.prototype.isAppliedTo = function(reservationId) {
            return _.some(this.appliedTo, { reservation: reservationId });
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#clear
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Clear all applications
         */
        ShopCoupon.prototype.clear = function() {
            this.appliedTo = [];
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#applyToProduct
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Apply coupon to product
         *
         * @param {Product} product Type of product
         * @param {String} productReservationGuid Reservation for aforementioned product
         */
        ShopCoupon.prototype.applyToProduct = function(product, productReservationGuid) {
            if(!this.appliesToProduct(product))
                return;
            this.appliedTo.push({
                productGuid: product.guid,
                reservation: productReservationGuid
            });
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#calculateProductPrice
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Calculate price for a product
         *
         * @param {Product} product Type of product
         * @param {String} [productReservationGuid] Reservation for aforementioned product.
         * If not provided, then method won't check if coupon is applied to product.
         * @returns {Number} New price if coupon has been applied to this product
         */
        ShopCoupon.prototype.calculateProductPrice = function(product, productReservationGuid) {
            let discount = this.calculateProductDiscount(product, productReservationGuid);
            return Math.max(0, product.ticketPrice - discount);
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#calculateProductDiscount
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Calculate discount for a product
         *
         * @param {Product} product Type of product
         * @param {String} [productReservationGuid] Reservation for aforementioned product.
         * If not provided, then method won't check if coupon is applied to product.
         * @returns {Number} Discount for a product
         */
        ShopCoupon.prototype.calculateProductDiscount = function(product, productReservationGuid) {
            if(productReservationGuid && !_.find(this.appliedTo, { reservation: productReservationGuid }))
                return 0;
            let price = product.ticketPrice;
            let applicable = _.find(this.coupon.products, { guid: product.guid });
            if(!applicable)
                return 0;

            let amount = parseFloat(_.get(applicable, 'pivot.amount_override') || this.coupon.amount);

            switch(this.coupon.type) {
                case 'new-price':
                    return product.ticketPrice - amount;
                case 'fixed-discount':
                    return amount;
                case 'percentage-discount':
                    return price * (amount / 100);
                default:
                    return 0;
            }
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#calculatePricesForProductSet
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Calculate the prices for individual products in a set of products
         *
         * Takes into account how many times this coupon is allowed, and how
         * many are already used
         *
         * @param {Product[]} products Set of products attached to a ticket
         * @returns {Object} Prices, keyed by product guid
         */
        ShopCoupon.prototype.calculatePricesForProductSet = function(products) {
            let discounts = this.calculateDiscountsForProductSet(products);
            let prices = _.mapValues(discounts, (discount, guid) => {
                let product = _.find(products, { guid: guid });
                return Math.max(0, product.ticketPrice - discount);
            });
            return prices;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#calculateTotalProductSetPrice
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Calculate the total price for a set of products
         *
         * Takes into account how many times this coupon is allowed, and how
         * many are already used
         *
         * @param {Product[]} products Set of products attached to a ticket
         * @returns {Number} Total (discounted) price of product set
         */
        ShopCoupon.prototype.calculateTotalProductSetPrice = function(products) {
            let prices = this.calculatePricesForProductSet(products);
            let total = _.sum(_.values(prices));
            return total;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#calculateDiscountsForProductSet
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Calculate the discounts for individual products in a set of products
         *
         * Takes into account how many times this coupon is allowed, and how
         * many are already used
         *
         * @param {Product[]} products Set of products attached to a ticket
         * @returns {Object} Discounts, keyed by product guid
         */
        ShopCoupon.prototype.calculateDiscountsForProductSet = function(products) {
            products = _.orderBy(products, ['ticketPrice'], ['desc']);

            // We need to keep track of how many times we can apply
            // coupon to products within this set. There's a total
            // maximum, and a maximum per product

            // -1 means infinite during this calculation, whereas API
            // responds with 0 being infinite.
            let availableSlots = {
                total: this.applies_to_count === 0 ? -1 : (this.applies_to_count - this.appliedTo.length)
            };
            _.forEach(this.coupon.products, product => {
                if (this.coupon.type === 'new-price' && product.price < this.coupon.amount) {
                    return;
                }

                let max = _.get(product, 'pivot.max_per_coupon', 0);
                if(max === 0) {
                    availableSlots[product.guid] = -1;
                    return;
                }
                let alreadyTaken = _.filter(this.appliedTo, {productGuid: product.guid}).length;
                availableSlots[product.guid] = Math.max(0, max - alreadyTaken);
            });

            let discounts = {};
            _.forEach(products, (product) => {
                // All slots taken
                if(availableSlots.total === 0)
                    return discounts[product.guid] = 0;

                // All product slots taken or coupon doesn't apply
                if(!_.has(availableSlots, product.guid) || availableSlots[product.guid] === 0)
                    return discounts[product.guid] = 0;

                // Update slots, either preserving the -1 or decrementing the counter
                availableSlots[product.guid] = Math.max(-1, availableSlots[product.guid] - 1);
                availableSlots.total = Math.max(-1, availableSlots.total - 1);
                return discounts[product.guid] = this.calculateProductDiscount(product);
            });
            return discounts;
        };


        /**
         * @ngdoc method
         * @name eventix.common.models.ShopCoupon#reapplyAcrossReservations
         * @methodOf eventix.common.models.ShopCoupon
         * @description
         * Redistribute coupon across products on reservation, maximizing discount for customer
         *
         * @param {TicketReservation[]} reservations Array of ticketReservations currently in shopping cart
         */
        ShopCoupon.prototype.reapplyAcrossReservations = function(reservations) {
            this.appliedTo = [];
            let productMap = [];
            let applicable = _.map(this.coupon.products, 'guid');

            // Build array of products across reservations that are eligible to receive discount
            _.forEach(reservations, reservation => {
                let products = _.keyBy(reservation.products, 'guid');
                for(let productReservation of reservation.productReservations) {
                    // Product not eligible
                    if(!_.includes(applicable, productReservation.productId))
                        continue;
                    let product = products[productReservation.productId];
                    productMap.push({
                        productReservation: productReservation.guid,
                        product: product,
                        price: product.ticketPrice
                    });

                }
            });
            productMap = _.orderBy(productMap, ['price'], ['desc']);
            _.forEach(productMap, candidate => {
                // We can blindly call apply and let that function sort out
                // applies_to_count limits
                this.applyToProduct(candidate.product, candidate.productReservation);
            });
        };

        return ShopCoupon;
    });
}
